import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AMENITIES_LIST } from '../constants';

// Adjustable price range slider bounds & step (VND)
const PRICE_MIN = 0;
const PRICE_MAX = 5000000; // 5,000,000₫
const PRICE_STEP = 500000; // each adjust step = 500,000₫

const formatVnd = (value) => new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(value);

const FilterBar = ({ filters, setFilters, onReset }) => {
  const { t } = useTranslation();
  // Collapsed by default; auto-expanded if a price/amenity filter is active
  const [expanded, setExpanded] = useState(
    Boolean(filters.minPrice || filters.maxPrice || filters.amenities.length > 0)
  );

  const minPriceNum = filters.minPrice ? parseInt(filters.minPrice, 10) : PRICE_MIN;
  const maxPriceNum = filters.maxPrice ? parseInt(filters.maxPrice, 10) : PRICE_MAX;

  const handleMinPriceChange = (value) => {
    const next = Math.min(parseInt(value, 10), maxPriceNum - PRICE_STEP);
    setFilters((prev) => ({ ...prev, minPrice: next > PRICE_MIN ? String(next) : '' }));
  };

  const handleMaxPriceChange = (value) => {
    const next = Math.min(Math.max(value, PRICE_MIN + PRICE_STEP), PRICE_MAX);
    setFilters((prev) => ({ ...prev, maxPrice: next >= PRICE_MAX ? '' : String(next) }));
  };

  const handleAmenityToggle = (amenityKey) => {
    setFilters((prev) => {
      const amenities = prev.amenities.includes(amenityKey)
        ? prev.amenities.filter((a) => a !== amenityKey)
        : [...prev.amenities, amenityKey];
      return { ...prev, amenities };
    });
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
          <svg className="w-5 h-5 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          <span>{t('filter.title')}</span>
        </h3>
        <button
          onClick={onReset}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium hover:underline"
        >
          {t('filter.reset')}
        </button>
      </div>

      {/* Expand / collapse toggle */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-primary-600 font-medium transition-colors mb-4"
        aria-expanded={expanded}
      >
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
        <span>{expanded ? t('filter.hideFilters') : t('filter.showFilters')}</span>
      </button>

      {/* Filter content — collapsible */}
      {expanded && (
        <div className="space-y-4">
      {/* Adjustable price range slider */}
      <div className="mb-4">
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">{t('filter.priceRange')}</p>
        <div className="px-1">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-semibold text-primary-600">
              {formatVnd(minPriceNum)}₫
            </span>
            <span className="text-xs text-gray-400 mx-2">—</span>
            <span className="text-sm font-semibold text-primary-600">
              {formatVnd(maxPriceNum)}₫{maxPriceNum >= PRICE_MAX ? '+' : ''}
            </span>
          </div>
          {/* Track with active range highlight */}
          <div className="relative h-6 flex items-center">
            <div className="absolute w-full h-1.5 rounded-full bg-gray-200" />
            <div
              className="absolute h-1.5 rounded-full bg-primary-500"
              style={{
                left: `${(minPriceNum / PRICE_MAX) * 100}%`,
                right: `${100 - (maxPriceNum / PRICE_MAX) * 100}%`,
              }}
            />
            {/* Min handle */}
            <input
              type="range"
              min={PRICE_MIN}
              max={PRICE_MAX - PRICE_STEP}
              step={PRICE_STEP}
              value={minPriceNum}
              onChange={(e) => handleMinPriceChange(parseInt(e.target.value, 10))}
              aria-label={t('filter.minPrice')}
              className="absolute w-full appearance-none bg-transparent pointer-events-none h-0 focus:outline-none
                [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-4 [&::-webkit-slider-thumb]:border-primary-500
                [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:z-20"
            />
            {/* Max handle */}
            <input
              type="range"
              min={PRICE_MIN}
              max={PRICE_MAX}
              value={maxPriceNum}
              onChange={(e) => handleMaxPriceChange(parseInt(e.target.value, 10))}
              aria-label={t('filter.maxPrice')}
              className="absolute w-full appearance-none bg-transparent h-6 focus:outline-none
                [&::-webkit-slider-thumb]:pointer-events-auto
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-4 [&::-webkit-slider-thumb]:border-primary-500
                [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:z-30"
              style={{ pointerEvents: 'none' }}
            />
          </div>
        </div>
      </div>

      {/* Amenities Filter */}
      <div>
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">
          {t('about.form.amenities') || 'Amenities'}
        </p>
        <div className="flex flex-wrap gap-2">
          {AMENITIES_LIST.map((amenity) => {
            const isSelected = filters.amenities.includes(amenity.key);
            return (
              <button
                key={amenity.key}
                type="button"
                onClick={() => handleAmenityToggle(amenity.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 text-sm font-medium transition-all ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50 text-primary-700 shadow-sm'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span className="text-base leading-none">{amenity.icon}</span>
                <span>{t(`amenity.${amenity.key}`)}</span>
                {isSelected && (
                  <svg className="w-3.5 h-3.5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      </div>
      </div>
      )}
    </div>
  );
};

export default FilterBar;
